import React, { useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';

const TYPES = [
  'Missing_Drawing',
  'Material_Shortage',
  'Late_Inspection',
  'Permit_Delay',
  'Equipment_Breakdown',
  'Weather',
  'Labour',
  'Design_Issue',
  'Vendor_Delay',
  'Client_Hold',
  'Other',
];
const STATUSES = ['Open', 'Mitigating', 'Resolved', 'Closed'];
const SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];

type Props = { projectId?: string };

export const ConstraintsPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const packagesQ = useQuery({
    queryKey: ['cep-packages', projectId],
    queryFn: () => epcService.getExecutionPackages(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const constraintsQ = useQuery({
    queryKey: ['cep-constraints', projectId],
    queryFn: () => epcService.getCepConstraints(projectId),
    enabled: !!projectId,
    staleTime: 15_000,
  });

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createConstraint({ ...values, projectId }),
    onSuccess: () => {
      message.success('Constraint logged (EOIP advisory if High/Critical)');
      setOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['cep-constraints'] });
      void qc.invalidateQueries({ queryKey: ['cep-work-fronts'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      epcService.patchConstraint(id, { status }),
    onSuccess: () => {
      message.success('Constraint updated');
      void qc.invalidateQueries({ queryKey: ['cep-constraints'] });
      void qc.invalidateQueries({ queryKey: ['cep-work-fronts'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) return <Tag>Select a project for the constraint register</Tag>;

  const rows = (constraintsQ.data as any[]) || [];
  const packages = (packagesQ.data as any[]) || [];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" onClick={() => setOpen(true)}>
          Log constraint
        </Button>
        <Tag color="geekblue">CEP Wave 3</Tag>
        <Tag>Advisory EOIP only — never posts finance</Tag>
      </Space>
      <Table
        size="small"
        rowKey={(r) => String(r.id)}
        loading={constraintsQ.isLoading}
        dataSource={rows}
        columns={[
          { title: 'Type', dataIndex: 'constraint_type', width: 150 },
          { title: 'Title', dataIndex: 'title', ellipsis: true },
          {
            title: 'Severity',
            dataIndex: 'severity',
            width: 100,
            render: (v: string) => (
              <Tag color={v === 'Critical' || v === 'High' ? 'red' : 'default'}>{v}</Tag>
            ),
          },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 140,
            render: (v: string, r: any) => (
              <Select
                size="small"
                style={{ width: 130 }}
                value={v}
                options={STATUSES.map((s) => ({ value: s, label: s }))}
                onChange={(status) => patchMut.mutate({ id: String(r.id), status })}
              />
            ),
          },
          {
            title: 'EOIP',
            width: 70,
            render: (_: unknown, r: any) =>
              r.eoip_signal_json ? <Tag color="purple">signal</Tag> : <Tag>—</Tag>,
          },
        ]}
      />
      <Modal
        title="Log constraint"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => createMut.mutate(v)}
          initialValues={{ status: 'Open', severity: 'Medium' }}
        >
          <Form.Item name="constraintType" label="Type" rules={[{ required: true }]}>
            <Select options={TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="packageId" label="Execution package">
            <Select
              allowClear
              options={packages.map((p: any) => ({
                value: String(p.id),
                label: p.package_number,
              }))}
            />
          </Form.Item>
          <Form.Item name="severity" label="Severity">
            <Select options={SEVERITIES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select options={STATUSES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ConstraintsPanel;
